import React, { useCallback, useState } from 'react';
import moment from 'moment/moment';
import { useQuery } from '@apollo/client';
import { QueryTableResponse } from 'global';
import { DateFormats } from 'constants/common';
import { QuoteChangeLogChangeType } from '@trnsact/trnsact-shared-types';
import { ChangelogFiltersInput, ChangelogListing } from 'components/shared/changelog';

import { quoteAPI } from '../../../api';
import { QuoteChangesCell } from './cells';
import { QuoteAPINameSpase } from '../../../api/types';

interface Props {
  quoteId: string;
}

export const QuoteChangelogListing = ({ quoteId }: Props) => {
  const [pagination, setPagination] = useState({ page: 1, perPage: 25 });
  const [filters, setFilters] = useState<ChangelogFiltersInput | undefined>(undefined);

  const { data, previousData, loading, refetch } = useQuery<QuoteAPINameSpase<QueryTableResponse<Record<string, any>>>>(
    quoteAPI.getQuoteChangelogList,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        input: {
          pagination,
          ...(filters ? { filters } : {}),
          ...(quoteId ? { parentId: quoteId } : {}),
        },
      },
    }
  );

  const handleFiltersChanged = useCallback((nextFilters: ChangelogFiltersInput | undefined) => {
    setFilters(nextFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const items = data?.quote?.result?.items ?? previousData?.quote?.result?.items ?? [];

  return (
    <ChangelogListing<Record<string, any>, typeof QuoteChangeLogChangeType>
      list={items}
      loading={loading}
      refetch={refetch}
      pagination={pagination}
      events={QuoteChangeLogChangeType}
      onPaginationChanged={setPagination}
      onFiltersChanged={handleFiltersChanged}
      changesCell={({ changes, changeType }) => <QuoteChangesCell changeType={changeType} changes={changes} />}
      total={data?.quote?.result?.pagination?.total ?? 0}
      mapper={items =>
        items.map(log => ({
          id: log.id,
          changes: log.changes,
          changeType: log.changeType,
          modifiedBy: log.modifiedBy?.fullName ?? '—',
          createdDateTime: moment(log.createdDateTime).format(DateFormats.DateTimeMedium24Hour),
        }))
      }
    />
  );
};
